import styled, { keyframes } from 'styled-components';
import type { OfferTone } from '../../lib/offers';

const MUTED = 'oklch(0.44 0.028 257.42)';

/** خلفية البطاقة */
const CARD_BG = 'oklch(0.97 0.02 12)';
/** لون النص والحدود والأيقونة */
const ACCENT = 'oklch(0.51 0.20 17)';
const CARD_EDGE = ACCENT;
/** نص الوصف أسفل العنوان */
const CARD_MUTED = 'oklch(0.13 0.04 265)';

/**
 * البطاقات تشترك في نفس المخطط اللوني، فكل النغمات تشير إليه.
 * أُبقي على الخريطة لتسهيل تخصيص لون لبطاقة بعينها لاحقاً دون تغيير المكوّنات.
 */
const TONE = { fg: ACCENT, soft: 'oklch(0.51 0.20 17 / 0.12)' };
export const TONES: Record<OfferTone, { fg: string; soft: string }> = {
  green: TONE,
  blue: TONE,
  rose: TONE,
  amber: TONE,
};

export const StripSection = styled.section<{ $compact?: boolean }>`
  direction: rtl;
  width: 100%;
  margin: ${(props) => (props.$compact ? '0 0 16px' : '0 0 28px')};
`;

export const StripTitle = styled.h4`
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-color);
`;

export const StripScroller = styled.div<{ $compact?: boolean }>`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 2px;

  &::-webkit-scrollbar {
    display: none;
  }

  /* أول بطاقة تبدأ عند حدّ المحتوى تماماً، والبقية تُطل من الحافة المقابلة */
  @media (max-width: 768px) {
    margin-inline-end: ${(props) => (props.$compact ? '0' : '-20px')};
    padding-inline-end: ${(props) => (props.$compact ? '0' : '20px')};
  }
`;

const GAP = 10;
/** أقل عرض يبقى النص فيه مقروءاً — لذلك قد يظهر أقل من 2.5 بطاقة على الشاشات الضيقة */
const MIN_CARD = 160;

/**
 * عرض البطاقة على الجوال حسب عددها:
 * بطاقة واحدة تملأ العرض، بطاقتان تظهران كاملتين، وثلاث فأكثر ≈ 2.5 بطاقة مع تمرير.
 */
const mobileSlotWidth = (count: number): string => {
  if (count <= 1) return '100%';
  if (count === 2) return `calc((100% - ${GAP}px) / 2)`;
  return `max(calc((100% - ${GAP * 2}px) / 2.5), ${MIN_CARD}px)`;
};

export const Slot = styled.div<{ $compact?: boolean; $count: number }>`
  flex: 0 0 auto;
  width: ${(props) =>
    props.$compact && props.$count <= 1 ? '100%' : props.$compact ? '225px' : '215px'};
  scroll-snap-align: start;

  @media (max-width: 768px) {
    width: ${(props) => mobileSlotWidth(props.$count)};
  }
`;

export const Frame = styled.div`
  background: transparent;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

/** البطاقة فوق الشريط الرمادي، فالقصّة الدائرية تكشف خلفية الصفحة لا الرمادي */
export const TicketStack = styled.div<{ $tone: OfferTone; $applied?: boolean }>`
  position: relative;
  z-index: 1;
  flex: 1;
  --edge: ${(props) => (props.$applied ? TONES[props.$tone].fg : CARD_EDGE)};
`;

/** طبقات خلف البطاقة توحي بعرض قابل للتكرار (كما في الصورة) */
export const StackLayer = styled.span<{ $depth: number }>`
  position: absolute;
  z-index: 0;
  top: ${(props) => props.$depth * 6}px;
  bottom: ${(props) => props.$depth * 6}px;
  inset-inline-start: ${(props) => props.$depth * -5}px;
  width: 24px;
  background: ${CARD_BG};
  border: 1px solid var(--edge);
  border-radius: 12px;
`;

export const Ticket = styled.div<{ $tone: OfferTone; $applied?: boolean; $interactive?: boolean }>`
  --notch: 8px;
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  /* محاذاة للأعلى: العنوان في نفس الارتفاع بكل البطاقات مهما اختلف طول الوصف */
  align-items: flex-start;
  gap: 10px;
  padding: 12px 13px;
  background: ${CARD_BG};
  /* حدّ 1px مطابق لباج التقييم */
  border: 1px solid var(--edge);
  border-radius: 12px;
  /* الانتشار السالب يسحب الظل للداخل فلا يظهر شيء منه أعلى البطاقة — ظلّ سفلي فقط */
  box-shadow: 0 3px 5px -2px oklch(0.2077 0.0398 265.75 / 0.13);
  text-align: start;
  width: 100%;
  font-family: inherit;
  cursor: ${(props) => (props.$interactive ? 'pointer' : 'default')};
  transition: transform 0.15s ease;

  /**
   * القصّة الجانبية: قرص بلون خلفية الصفحة وحدّه بلون حدّ البطاقة، مركزه على حافة البطاقة،
   * ويُقصّ نصفه الخارجي بـ clip-path. القص وحدّه شكل واحد فيتطابقان تماماً بلا فجوة.
   * الإزاحة = نصف القطر + 1px لأنها تُحسب من صندوق الحشو بينما الحافة هي صندوق الحدّ.
   */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: calc(var(--notch) * 2);
    height: calc(var(--notch) * 2);
    transform: translateY(-50%);
    background: var(--bg-color);
    border: 1px solid var(--edge);
    border-radius: 50%;
    pointer-events: none;
  }

  /* الإزاحة = نصف القطر + سُمك الحدّ، لأنها تُقاس من صندوق الحشو بينما الحافة هي صندوق الحدّ.
     بذلك يقع منتصف القرص على الحافة تماماً: يغطي خطّ الحدّ فيفتحه، ولا يبرز خارج البطاقة. */
  &::before {
    left: calc(-1 * var(--notch) - 2px);
    clip-path: inset(0 0 0 50%);
  }

  &::after {
    right: calc(-1 * var(--notch) - 2px);
    clip-path: inset(0 50% 0 0);
  }

  ${(props) =>
    props.$interactive &&
    `
    &:active { transform: scale(0.99); }
  `}

  &:focus-visible {
    outline: 2px solid ${(props) => TONES[props.$tone].fg};
    outline-offset: 2px;
  }
`;

export const TicketBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

/** أيقونة صغيرة ملاصقة للعنوان بدل فقاعة جانبية */
export const TitleIcon = styled.span<{ $tone: OfferTone }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${(props) => TONES[props.$tone].fg};
`;

export const Title = styled.div<{ $tone: OfferTone }>`
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.4;
  color: ${(props) => TONES[props.$tone].fg};
`;

export const Subtitle = styled.div`
  margin-top: 3px;
  font-size: 0.6875rem;
  line-height: 1.45;
  color: ${CARD_MUTED};
`;

/** شارة الزاوية مثل "1/2" */
export const Badge = styled.span`
  position: absolute;
  inset-inline-end: 0;
  bottom: 0;
  padding: 2px 8px;
  font-size: 0.625rem;
  font-weight: 700;
  color: oklch(1 0 0);
  background: oklch(0.75 0.09 190);
  border-start-start-radius: 14px;
  border-end-end-radius: 17px;
`;

export const ProgressTrack = styled.div`
  margin-top: 8px;
  height: 4px;
  border-radius: 999px;
  background: oklch(0.51 0.2 17 / 0.18);
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $tone: OfferTone; $value: number }>`
  height: 100%;
  width: ${(props) => Math.round(props.$value * 100)}%;
  border-radius: 999px;
  background: ${(props) => TONES[props.$tone].fg};
  transition: width 0.35s ease;
`;

export const StatusText = styled.div<{ $tone: OfferTone; $applied?: boolean }>`
  margin-top: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${(props) => (props.$applied ? TONES.green.fg : TONES[props.$tone].fg)};
`;

/**
 * الشريط الرمادي أسفل البطاقة. الامتداد للأعلى وحشوة الأعلى يزيدان معاً بنفس المقدار
 * فيرتفع الشريط دون أن يتحرك النص. الامتداد محدود كي لا يصل الرمادي إلى القصّة الدائرية.
 */
export const FrameFooter = styled.div`
  margin-top: -24px;
  padding: 30px 10px 9px;
  background: var(--gray-bg);
  border-radius: 0 0 14px 14px;
  text-align: center;
  font-size: 0.6875rem;
  color: ${MUTED};
`;

const shimmer = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

export const Skeleton = styled.div`
  height: 118px;
  border-radius: 12px;
  background: var(--gray-bg);
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;
